import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import NoSelectionPrompt from "../../components/NoSelectionPrompt.tsx";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import CopyButton from "../../components/CopyButton.tsx";
import DetailRow from "../../components/DetailRow.tsx";
import FormattedDate from "../../components/FormattedDate";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import Panel from "../../components/Panel.tsx";
import { userHasOrgScope } from "../../auth/scopes";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import PageHeader from "../../components/PageHeader.tsx";

const TITLE = "Organization details";

function DetailsPage() {
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const canEdit = userHasOrgScope(user, selectedOrgId, "org:write");

  const [name, setName] = useState("");

  const org = useQuery({
    queryKey: keys.organization.detail(selectedOrgId ?? ""),
    queryFn: () => api.getOrganization(selectedOrgId!),
    enabled: !!selectedOrgId,
  });

  useEffect(() => {
    if (org.data) {
      setName(org.data.name);
    }
  }, [org.data]);

  const updateMutation = useMutation({
    mutationFn: (newName: string) =>
      api.updateOrganization(selectedOrgId!, { name: newName }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(keys.organization.detail(selectedOrgId!), updated);
      queryClient.invalidateQueries({ queryKey: keys.organization.detail(selectedOrgId!) });
      // Refresh the auth user so the sidebar's `currentOrg.name` reflects the new value.
      await refreshUser();
      toast.success("Organization updated");
    },
  });

  if (!selectedOrgId) {
    return (
      <>
        <PageHeader title={TITLE} />
        <NoSelectionPrompt kind="org" />
      </>
    );
  }

  const orgName = org.data?.name ?? currentOrg?.name ?? "this organization";
  const header = (
    <PageHeader title={TITLE} description={`View and edit basic information about ${orgName}`} />
  );

  if (org.isLoading) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        {header}
        <CenteredSpinner />
      </Box>
    );
  }

  if (org.isError || !org.data) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        {header}
        <Alert severity="error">Failed to load organization.</Alert>
      </Box>
    );
  }

  const trimmedName = name.trim();
  const isDirty = trimmedName !== org.data.name;
  const canSave = canEdit && isDirty && trimmedName.length > 0;

  return (
    <Box sx={{ maxWidth: 720 }}>
      {header}
      <Stack spacing={3}>
        <Panel title="General">
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={!canEdit || updateMutation.isPending}
              helperText={
                canEdit
                  ? "The display name for your organization."
                  : "Only organization admins can change this."
              }
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <Box>
              <Button
                variant="contained"
                disabled={!canSave}
                loading={updateMutation.isPending}
                onClick={() => updateMutation.mutate(trimmedName)}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </Panel>
        <Panel title="Metadata">
          <DetailRow
            label="Organization ID"
            value={
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
                  {org.data.id}
                </Box>
                <CopyButton text={org.data.id} label="Copy organization ID" sx={{ my: -0.5 }} />
              </Box>
            }
          />
          <DetailRow label="Created" value={<FormattedDate value={org.data.created_at} format="datetime" />} />
          <DetailRow
            label="Last updated"
            value={<FormattedDate value={org.data.updated_at} format="datetime" />}
          />
        </Panel>
      </Stack>
    </Box>
  );
}

export default DetailsPage;
